/**
 * Embed adoption — how many OTHER sites load a Fridge.com widget (FRIDGE-807).
 *
 * One number for /widgets: "Embedded on N sites", shown only when N is greater
 * than zero — a zero is the absence of a measurement, never a headline. The
 * source is the `embed_widget_view` event the framed widgets capture
 * (app/embed/_components/embed-view-capture.tsx) with the embedding page's
 * ORIGIN in `referrer_origin` (scheme + host only, never a path).
 *
 * The rule for what counts as an embedding site lives in ONE place —
 * `isEmbeddingOrigin` — and is applied to the origins PostHog returns, so the
 * query stays a plain group-by and the exclusions are unit-tested:
 *   • `unknown` (no referrer: a publisher sending `no-referrer`, or the widget
 *     opened top-level) is a load, not a site;
 *   • fridge.com itself (self-previews on the source pages, and the /widgets
 *     modal) is not another site;
 *   • loopback and localhost origins are development and test harnesses.
 *
 * Reads PostHog through the query API with the personal key (the same call
 * shape as app/api/blogs/trending/route.ts). Any missing key, non-200, parse
 * error or timeout yields `null`, and `null` renders nothing — the page never
 * shows a number it did not measure.
 */

/** Trailing window the count is measured over. */
export const EMBED_ADOPTION_WINDOW_DAYS = 90

const OWN_OR_LOCAL_ORIGIN = /^https?:\/\/(?:(?:www\.)?fridge\.com|localhost|127\.0\.0\.1|\[::1\]|0\.0\.0\.0)(?::\d+)?$/i

/** True when a `referrer_origin` value names a site other than Fridge.com. */
export function isEmbeddingOrigin(origin: string | null | undefined): boolean {
  if (!origin || origin === 'unknown') return false
  if (!/^https?:\/\/[^/]+$/i.test(origin)) return false
  return !OWN_OR_LOCAL_ORIGIN.test(origin)
}

/** HogQL: one row per referrer origin that loaded a FRAMED widget in the window. */
export function buildEmbeddingOriginsQuery(days: number = EMBED_ADOPTION_WINDOW_DAYS): string {
  return [
    'SELECT properties.referrer_origin AS origin, count() AS loads',
    'FROM events',
    "WHERE event = 'embed_widget_view'",
    `  AND timestamp >= now() - INTERVAL ${Math.max(1, Math.floor(days))} DAY`,
    "  AND toString(properties.embedded) = 'true'",
    'GROUP BY origin',
    'ORDER BY loads DESC',
    'LIMIT 500',
  ].join('\n')
}

/** Distinct embedding sites in a HogQL result set (`results` rows: [origin, loads]). */
export function countEmbeddingSites(rows: unknown): number | null {
  if (!Array.isArray(rows)) return null
  const sites = new Set<string>()
  for (const row of rows) {
    const origin = Array.isArray(row) ? row[0] : null
    if (typeof origin === 'string' && isEmbeddingOrigin(origin)) sites.add(origin.toLowerCase())
  }
  return sites.size
}

/** The hero line, or null when there is nothing measured to say. */
export function embeddedSitesLine(count: number | null | undefined): string | null {
  if (!count || count < 1) return null
  return count === 1 ? 'Embedded on 1 site' : `Embedded on ${count.toLocaleString('en-US')} sites`
}

function projectId(): string {
  // The same fallback app/api/blogs/trending/route.ts uses — a project id is not a secret.
  return process.env.POSTHOG_PROJECT_ID || '232740'
}

/**
 * Live count of sites embedding a widget over the trailing window; null when
 * it cannot be measured. Cached with the page (`next.revalidate`).
 */
export async function fetchEmbeddingSiteCount(): Promise<number | null> {
  const personalApiKey = process.env.POSTHOG_PERSONAL_API_KEY
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'
  if (!personalApiKey) return null
  try {
    const response = await fetch(`${host}/api/projects/${projectId()}/query`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${personalApiKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: { kind: 'HogQLQuery', query: buildEmbeddingOriginsQuery() } }),
      signal: AbortSignal.timeout(6_000),
      next: { revalidate: 3600 },
    })
    if (!response.ok) return null
    const data: { results?: unknown } = await response.json()
    return countEmbeddingSites(data?.results)
  } catch {
    return null
  }
}
